import React, { useState, useEffect, useCallback } from 'react';
import './SessionManager.css';

const SessionManager = ({ 
  onLogout, 
  autoLogoutTime = 30 * 60 * 1000, // 기본 30분
  showInNavigation = false,
  showInHeader = false 
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [remainingTime, setRemainingTime] = useState(autoLogoutTime);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // 현재 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 활동시간 업데이트
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    setRemainingTime(autoLogoutTime);
    localStorage.setItem('lastActivity', now.toString());
  }, [autoLogoutTime]);

  // 남은 시간 계산
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const timeDiff = now - lastActivity;
      const remaining = Math.max(0, autoLogoutTime - timeDiff);
      
      setRemainingTime(remaining);
      
      if (remaining <= 0) {
        alert('장시간 비활성 상태로 인해 자동 로그아웃됩니다.');
        onLogout();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lastActivity, autoLogoutTime, onLogout]);

  // 사용자 활동 감지
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    
    const handleActivity = () => {
      updateActivity();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateActivity(); // 탭이 다시 활성화될 때 활동 업데이트
      }
    };

    const handleFocus = () => {
      updateActivity(); // 윈도우가 포커스를 받을 때 활동 업데이트
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // 탭 포커스 이벤트 추가
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // 초기 활동시간 설정
    updateActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [updateActivity]);

  // 시간 포맷팅 함수
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // 시간 색상 결정
  const getTimeColor = () => {
    const minutes = Math.floor(remainingTime / (60 * 1000));
    if (minutes <= 5) return '#e74c3c'; // 빨간색
    if (minutes <= 10) return '#f39c12'; // 주황색
    return '#27ae60'; // 초록색
  };

  if (showInNavigation) {
    return (
      <div className="session-navigation">
        <span className="session-time-inline">
          🕐 {currentTime.toLocaleTimeString('ko-KR')} | 📅 {currentTime.toLocaleDateString('ko-KR')} | 
          <span 
            className="remaining-time-inline"
            style={{ color: getTimeColor() }}
          >
            ⏰ 자동로그아웃 {formatTime(remainingTime)} 남음
          </span>
        </span>
      </div>
    );
  }

  if (showInHeader) {
    return (
      <div className="session-header">
        <div className="session-info-inline">
          <span>🕐 {currentTime.toLocaleTimeString('ko-KR')} | 📅 {currentTime.toLocaleDateString('ko-KR')} | </span>
          <span 
            className="session-status-inline"
            style={{ color: getTimeColor() }}
          >
            ⏰ 자동로그아웃 {formatTime(remainingTime)} 남음
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="session-widget">
      <div className="session-time">
        {currentTime.toLocaleTimeString('ko-KR')}
      </div>
      <div 
        className="session-remaining"
        style={{ color: getTimeColor() }}
      >
        ⏰ {formatTime(remainingTime)}
      </div>
    </div>
  );
};

export default SessionManager;